<script setup lang="ts">
import { type PropType, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useViewportSize } from '@/components/composables/useViewportSize'
import { useStore } from '@/stores'
import { API } from '@/modules/api'
import { ProgressSpinner } from 'primevue'

// Type declaration for AuthorizeNetPopup
declare global {
  interface Window {
    AuthorizeNetPopup: {
      options: {
        useTestEnvironment: boolean
        eCheckEnabled: boolean
        onPopupClosed: (() => void) | null
        skipZIndexCheck: boolean
      }
      openAddPaymentPopup: () => void
      closePopup: () => void
      onReceiveCommunication: ((queryString: string) => void) | null
    }
  }
}

const { isMdOrGreater } = useViewportSize()

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  districtId: {
    type: Number,
    required: true
  },
  authorizeTokenOverride: {
    type: String,
    default: ''
  },
  isSandboxOverride: {
    type: Boolean,
    default: undefined
  },
  tokenFetcher: {
    type: Function as PropType<() => Promise<{ token: string; isSandbox: boolean }>>,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'authorize-net-funding-info-saved'])

const store = useStore()
const scriptsLoaded = ref(false)
const tokenLoaded = ref(false)
const token = ref('')
const isSandbox = ref(false)
const isLoading = ref(true)
// DTA-1029: the payment form did not arrive (token request failed, scripts missing, or
// Authorize.Net never answered). Shown instead of a spinner that never stops.
const loadFailed = ref(false)
let answerTimer: ReturnType<typeof setTimeout> | null = null
const AUTHORIZE_FORM_TIMEOUT_MS = 20_000

const stopAnswerTimer = () => {
  if (answerTimer !== null) {
    clearTimeout(answerTimer)
    answerTimer = null
  }
}

// Parse query string helper function
const parseQuery = (queryString: string): Record<string, string> => {
  const query: Record<string, string> = {}
  const a = (queryString[0] === '?' ? queryString.substr(1) : queryString).split('&')
  for (let i = 0; i < a.length; i++) {
    const b = a[i].split('=')
    query[decodeURIComponent(b[0])] = decodeURIComponent(b[1] || '')
  }
  return query
}

// Setup communication handler for Authorize.Net iframe
const setupCommunicationHandler = () => {
  if (!window.AuthorizeNetPopup) return

  window.AuthorizeNetPopup.onReceiveCommunication = function (queryString: string) {
    const params = parseQuery(queryString)
    const authorizeIframe = document.getElementById('iframeAuthorizeNet') as HTMLIFrameElement

    switch (params['action']) {
      case 'successfulSave':
        emit('authorize-net-funding-info-saved')
        emit('update:modelValue', false)
        break
      case 'cancel':
        // Close dialog on cancel
        emit('update:modelValue', false)
        break
      case 'resizeWindow':
        // Resize iframe based on Authorize.Net communication
        const w = parseInt(params['width'] || '0')
        const h = parseInt(params['height'] || '0')
        if (authorizeIframe && w > 0 && h > 0) {
          authorizeIframe.style.width = w + 'px'
          authorizeIframe.style.height = h + 'px'
        }
        // Hide spinner when iframe is ready
        stopAnswerTimer()
        isLoading.value = false
        break
    }
  }
}

const closePopup = () => {
  if (window.AuthorizeNetPopup) {
    window.AuthorizeNetPopup.closePopup()
  }
}

const loadScript = (src: string): Promise<void> =>
  new Promise<void>(resolve => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = src
    script.onload = () => resolve()
    document.body.appendChild(script)
  })

const initializeAuthorizeNet = async () => {
  if (!tokenLoaded.value) {
    try {
      if (props.tokenFetcher) {
        const results = await props.tokenFetcher()
        token.value = results.token
        isSandbox.value = results.isSandbox
      } else if (props.authorizeTokenOverride) {
        token.value = props.authorizeTokenOverride
        isSandbox.value = props.isSandboxOverride ?? false
      } else {
        const results = await API.Get.authorizeIframe(props.districtId)
        token.value = results.token
        isSandbox.value = results.isSandbox
      }
      tokenLoaded.value = true
    } catch (err) {
      console.error(err)
      loadFailed.value = true
      return false
    }
  }

  // Load scripts if not already loaded
  if (!scriptsLoaded.value) {
    // Load dependencies (jQuery only if popup.js requires it)
    await loadScript('/js/common/jquery-1.10.2.js')
    await loadScript('/js/authorize/popup.js')

    if (!window.AuthorizeNetPopup) {
      loadFailed.value = true
      return false
    }
    if (isSandbox.value) {
      window.AuthorizeNetPopup.options.useTestEnvironment = true
    }
    window.AuthorizeNetPopup.options.eCheckEnabled = true

    // Setup communication handler
    setupCommunicationHandler()

    scriptsLoaded.value = true
  }

  return true
}

// Load the token and scripts, then open Authorize.Net's form in the iframe.
const openForm = async () => {
  isLoading.value = true
  loadFailed.value = false
  stopAnswerTimer()

  // Load scripts and fetch token when dialog opens (lazy loading)
  const initialized = await initializeAuthorizeNet()

  if (initialized && scriptsLoaded.value && tokenLoaded.value) {
    // Wait for Dialog component to render its content
    await nextTick()
    // Additional small delay to ensure all DOM elements are available
    setTimeout(() => {
      if (document.getElementById('iframeAuthorizeNet')) {
        window.AuthorizeNetPopup.openAddPaymentPopup()
        // DTA-1029: Authorize.Net answers with a resize once its form is drawn. No
        // answer means it refused the token or never loaded.
        answerTimer = setTimeout(() => {
          if (isLoading.value) {
            loadFailed.value = true
          }
        }, AUTHORIZE_FORM_TIMEOUT_MS)
      }
    }, 100)
  } else {
    loadFailed.value = true
  }
}

// Try again asks for a fresh token: the one that failed may have expired.
const retry = () => {
  tokenLoaded.value = false
  openForm()
}

// Watch for dialog opening - load scripts and open popup when dialog opens
watch(
  () => props.modelValue,
  async isOpen => {
    if (isOpen) {
      await openForm()
    } else {
      // Reset loading state when dialog closes
      stopAnswerTimer()
      isLoading.value = false
      loadFailed.value = false
    }
  },
  { immediate: true }
)

onBeforeUnmount(stopAnswerTimer)
</script>

<template>
  <Dialog
    :visible="modelValue"
    modal
    @update:visible="$emit('update:modelValue', $event)"
    :baseZIndex="99999"
    header="Payment Profile"
  >
    <div>
      <form
        id="formAuthorizeNetPopup"
        name="formAuthorizeNetPopup"
        method="post"
        target="iframeAuthorizeNet"
        style="display: none"
      >
        <input type="hidden" name="Token" :value="token" />
      </form>

      <!-- DTA-1029: the form could not be loaded; say so instead of spinning. -->
      <div
        v-if="loadFailed"
        class="flex flex-column align-items-center justify-content-center text-center p-3"
        style="min-height: 300px"
        role="alert"
        data-testid="authorize-load-failed"
      >
        <p class="mb-3">{{ $t('c.fundingForms.transitDialog.cardFormUnavailable') }}</p>
        <Button :label="$t('c.fundingForms.transitDialog.tryAgainLabel')" @click="retry()" />
      </div>

      <!-- Loading Spinner -->
      <div
        v-else-if="isLoading"
        class="flex flex-col align-items-center justify-content-center"
        style="min-height: 300px"
      >
        <ProgressSpinner class="w-4rem" stroke-width="5"></ProgressSpinner>
      </div>

      <!-- Popup -->
      <div
        v-show="!isLoading && !loadFailed"
        id="divAuthorizeNetPopup"
        class="AuthorizeNetPopupGrayFrameTheme"
      >
        <div class="AuthorizeNetPopupOuter">
          <div class="AuthorizeNetPopupTop">
            <div class="AuthorizeNetPopupClose">
              <a href="#" @click.prevent="closePopup()" title="Close"></a>
            </div>
          </div>

          <div class="AuthorizeNetPopupInner">
            <iframe
              name="iframeAuthorizeNet"
              id="iframeAuthorizeNet"
              src="/authorize/empty.html"
              frameborder="0"
              scrolling="no"
            ></iframe>
          </div>
        </div>

        <!-- Shadows -->
        <div class="AuthorizeNetShadow AuthorizeNetShadowT"></div>
        <div class="AuthorizeNetShadow AuthorizeNetShadowR"></div>
        <div class="AuthorizeNetShadow AuthorizeNetShadowB"></div>
        <div class="AuthorizeNetShadow AuthorizeNetShadowL"></div>
        <div class="AuthorizeNetShadow AuthorizeNetShadowTR"></div>
        <div class="AuthorizeNetShadow AuthorizeNetShadowBR"></div>
        <div class="AuthorizeNetShadow AuthorizeNetShadowBL"></div>
        <div class="AuthorizeNetShadow AuthorizeNetShadowTL"></div>
      </div>

      <div id="divAuthorizeNetPopupScreen" style="display: none"></div>
    </div>
  </Dialog>
</template>

<style scoped></style>
